"use client"

import { useEffect, useRef, useState } from "react"
import { ChevronLeft, ChevronRight, List, X } from "lucide-react"

import { Category } from "@/lib/types"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { ScrollArea } from "@/components/ui/scroll-area"

interface MenuCategoryTabsProps {
  categories: Category[]
  activeCategory: string
  onCategoryChange: (categoryId: string) => void
}

export function MenuCategoryTabs({
  categories,
  activeCategory,
  onCategoryChange,
}: MenuCategoryTabsProps) {
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const [scrollPosition, setScrollPosition] = useState(0)
  const [maxScroll, setMaxScroll] = useState(0)
  const activeTabRef = useRef<HTMLButtonElement>(null)
  const indicatorRef = useRef<HTMLDivElement>(null)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)

  // Update scroll info and track max scroll
  useEffect(() => {
    const updateScrollInfo = () => {
      if (scrollAreaRef.current) {
        // Find the scrollable element within the ScrollArea
        const scrollableElement = scrollAreaRef.current.querySelector(
          "[data-radix-scroll-area-viewport]"
        )
        if (scrollableElement) {
          setMaxScroll(
            scrollableElement.scrollWidth - scrollableElement.clientWidth
          )
        }
      }
    }

    updateScrollInfo()
    window.addEventListener("resize", updateScrollInfo)
    return () => window.removeEventListener("resize", updateScrollInfo)
  }, [categories])

  // Track scroll position
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setScrollPosition(e.currentTarget.scrollLeft)
  }

  // Update scroll position when scrolling programmatically
  useEffect(() => {
    // Set up scroll event listener to track position
    if (scrollAreaRef.current) {
      const scrollableElement = scrollAreaRef.current.querySelector(
        "[data-radix-scroll-area-viewport]"
      )

      if (scrollableElement) {
        // Check scroll position after any scroll operation
        scrollableElement.addEventListener("scroll", () => {
          setScrollPosition(scrollableElement.scrollLeft)
        })
      }
    }
  }, [])

  // Simple navigation functions
  const scrollPrev = () => {
    if (scrollAreaRef.current) {
      // Find the scrollable element within the ScrollArea
      const scrollableElement = scrollAreaRef.current.querySelector(
        "[data-radix-scroll-area-viewport]"
      )
      if (scrollableElement) {
        // Scroll by the width of the viewport
        const scrollAmount = scrollableElement.clientWidth
        scrollableElement.scrollBy({
          left: -scrollAmount,
          behavior: "smooth",
        })

        // Update scroll position after scrolling
        setTimeout(() => {
          setScrollPosition(scrollableElement.scrollLeft)
        }, 500)
      }
    }
  }

  const scrollNext = () => {
    if (scrollAreaRef.current) {
      // Find the scrollable element within the ScrollArea
      const scrollableElement = scrollAreaRef.current.querySelector(
        "[data-radix-scroll-area-viewport]"
      )
      if (scrollableElement) {
        // Scroll by the width of the viewport
        const scrollAmount = scrollableElement.clientWidth
        scrollableElement.scrollBy({
          left: scrollAmount,
          behavior: "smooth",
        })

        // Update scroll position after scrolling
        setTimeout(() => {
          setScrollPosition(scrollableElement.scrollLeft)
        }, 500)
      }
    }
  }

  const isAtStart = scrollPosition <= 10
  const isAtEnd = maxScroll > 0 && scrollPosition >= maxScroll - 10

  // Update indicator position when activeCategory changes
  useEffect(() => {
    if (activeTabRef.current && indicatorRef.current) {
      indicatorRef.current.style.left = `${activeTabRef.current.offsetLeft}px`
      indicatorRef.current.style.width = `${activeTabRef.current.offsetWidth}px`
    }
  }, [activeCategory])

  // Update indicator position on window resize
  useEffect(() => {
    const updateIndicatorPosition = () => {
      if (activeTabRef.current && indicatorRef.current) {
        indicatorRef.current.style.left = `${activeTabRef.current.offsetLeft}px`
        indicatorRef.current.style.width = `${activeTabRef.current.offsetWidth}px`
      }
    }

    window.addEventListener("resize", updateIndicatorPosition)

    // Initial position
    updateIndicatorPosition()

    return () => {
      window.removeEventListener("resize", updateIndicatorPosition)
    }
  }, [])

  // Scroll active tab into view when it changes
  useEffect(() => {
    if (activeTabRef.current) {
      activeTabRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      })
    }
  }, [activeCategory])

  // Handle category selection from popover
  const handleCategorySelect = (categoryId: string) => {
    onCategoryChange(categoryId)
    setIsPopoverOpen(false)
  }

  return (
    <nav className="sticky top-16 bg-white z-[5]" aria-label="Danh mục món ăn">
      <div className="flex items-center border-b border-gray-200">
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Xem tất cả danh mục"
            >
              <List size={17} strokeWidth={2.4} />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-72 p-0 shadow-2xl"
            align="start"
            sideOffset={8}
          >
            <div className="flex items-center justify-between px-4 py-2 pt-3">
              <h3 className="font-semibold text-xl">Tất cả danh mục</h3>
            </div>
            <ScrollArea className="h-[min(400px,calc(100vh-150px))]">
              <div className="py-1">
                {categories.map((category) => (
                  <button
                    key={`popover-${category.category_id}`}
                    onClick={() =>
                      handleCategorySelect(String(category.category_id))
                    }
                    className={cn(
                      "w-full text-left px-4 py-2.5 text-sm transition-colors relative",
                      activeCategory === String(category.category_id)
                        ? "bg-gray-200 font-semibold text-black before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 before:bg-black"
                        : "hover:bg-gray-100 text-gray-800"
                    )}
                  >
                    {category.category_name}
                  </button>
                ))}
              </div>
            </ScrollArea>
          </PopoverContent>
        </Popover>
        <ScrollArea
          ref={scrollAreaRef}
          className="w-full"
          onScrollCapture={handleScroll}
        >
          <div className="flex gap-4 w-full relative py-2 px-4" role="tablist">
            {/* Active tab indicator - absolute positioned line that moves */}
            <div
              ref={indicatorRef}
              className="absolute bottom-0 h-[4px] bg-black transition-all duration-300 ease-in-out"
              style={{ left: 0, width: 0 }}
              aria-hidden="true"
            />

            {categories.map((category) => (
              <button
                key={category.category_id}
                ref={
                  activeCategory === String(category.category_id)
                    ? activeTabRef
                    : null
                }
                onClick={() => onCategoryChange(String(category.category_id))}
                className={`flex items-center gap-2 px-1 py-2 text-xs font-semibold uppercase tracking-wider whitespace-nowrap transition-all ${
                  activeCategory === String(category.category_id)
                    ? "text-black"
                    : "text-gray-500"
                }`}
                role="tab"
                aria-selected={activeCategory === String(category.category_id)}
                aria-controls={`category-${category.category_id}`}
                id={`tab-${category.category_id}`}
              >
                {category.category_name}
              </button>
            ))}
          </div>
        </ScrollArea>

        {/* Prev and Next buttons for scrolling */}
        <div className="flex items-center px-2">
          <button
            onClick={scrollPrev}
            disabled={isAtStart}
            className={cn(
              "p-1.5 rounded-full border border-gray-200 hover:bg-gray-100 transition-colors mr-1",
              isAtStart ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
            )}
            aria-label="Previous categories"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={scrollNext}
            disabled={isAtEnd}
            className={cn(
              "p-1.5 rounded-full border border-gray-200 hover:bg-gray-100 transition-colors",
              isAtEnd ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
            )}
            aria-label="Next categories"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </nav>
  )
}
